import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicPlanCard } from "@/components/PublicPlanCard";
import { PUBLIC_PLANS, MOST_POPULAR, PLANS_DISCLAIMER } from "@/lib/publicPlans";
import { Building2, Check, Minus, ArrowRight } from "lucide-react";

const FAQ = [
  { q: "What's included in the monthly subscription?", a: PLANS_DISCLAIMER },
  { q: "Can I change or cancel my plan later?", a: "Yes. Once you have an account, you can manage, upgrade, or cancel your plan anytime from the Plans page in your dashboard, including the secure billing portal." },
  { q: "How do I get started?", a: "Choose a plan and create your property manager account. You can select and activate your plan from your dashboard right after signing up." },
  { q: "Do you serve my area?", a: "SJ Property Care actively serves a growing network of markets — see the Service Areas section on our homepage for current coverage." },
];

// Feature rows for the comparison table (derived only from existing tier data).
const COMPARE_ROWS = [
  { label: "Property limit", get: (p) => (p.property_limit ? `Up to ${p.property_limit}` : "Custom") },
  { label: "Maintenance request portal", has: () => true },
  { label: "Work order tracking", has: () => true },
  { label: "Photo documentation", has: () => true },
  { label: "Recurring maintenance", has: (p) => ["sj_portfolio_monthly", "sj_pro_monthly", "sj_enterprise"].includes(p.lookup_key) },
  { label: "Property turnover management", has: (p) => ["sj_portfolio_monthly", "sj_pro_monthly", "sj_enterprise"].includes(p.lookup_key) },
  { label: "Priority dispatch", has: (p) => ["sj_portfolio_monthly", "sj_pro_monthly", "sj_enterprise"].includes(p.lookup_key) },
  { label: "Portfolio reporting", has: (p) => ["sj_portfolio_monthly", "sj_pro_monthly", "sj_enterprise"].includes(p.lookup_key) },
  { label: "Advanced reporting", has: (p) => ["sj_pro_monthly", "sj_enterprise"].includes(p.lookup_key) },
  { label: "Multi-user access", has: (p) => ["sj_pro_monthly", "sj_enterprise"].includes(p.lookup_key) },
  { label: "Dedicated account support", has: (p) => ["sj_pro_monthly", "sj_enterprise"].includes(p.lookup_key) },
];

export default function PublicPlans() {
  const navigate = useNavigate();
  const getStarted = (plan) => {
    if (plan.amount === null) { navigate("/request-service"); return; }
    localStorage.setItem("sj_pending_plan", plan.lookup_key);
    navigate(`/register/client?plan=${plan.lookup_key}`);
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Header (matches homepage) */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
            <span className="font-head font-bold text-lg">SJ Property Care</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="text-white hover:bg-white/10 hidden sm:inline-flex" onClick={() => navigate("/login")}>Property Manager</Button>
            <Button data-testid="plans-nav-plans" variant="ghost" className="text-white hover:bg-white/10 hidden sm:inline-flex" onClick={() => navigate("/plans")}>Plans</Button>
            <Button variant="ghost" className="text-white hover:bg-white/10 hidden sm:inline-flex" onClick={() => navigate("/login")}>Contractor</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg" onClick={() => navigate("/request-service")}>Request Service</Button>
          </div>
        </div>
      </header>

      {/* Hero band */}
      <section className="bg-slate-950 pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-blue-400">Pricing</span>
          <h1 className="mt-3 font-head text-4xl sm:text-5xl font-semibold text-white">Property Care Plans Built for Every Portfolio</h1>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto">Pick the plan that fits your portfolio. Every plan includes your SJ Property Care maintenance platform.</p>
        </div>
      </section>

      {/* Cards */}
      <section className="max-w-7xl mx-auto px-6 -mt-10">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="public-plans-grid">
          {PUBLIC_PLANS.map((p) => <PublicPlanCard key={p.lookup_key} plan={p} popular={p.lookup_key === MOST_POPULAR} onGetStarted={getStarted} />)}
        </div>
        <p className="mt-6 text-xs text-slate-500 max-w-3xl">{PLANS_DISCLAIMER}</p>
      </section>

      {/* Comparison table */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-head text-2xl md:text-3xl font-medium text-slate-900">Compare all plans</h2>
        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm" data-testid="plans-compare-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left font-semibold text-slate-700 px-4 py-3">Feature</th>
                {PUBLIC_PLANS.map((p) => <th key={p.lookup_key} className="text-center font-semibold text-slate-900 px-4 py-3">{p.name}<div className="text-xs font-normal text-slate-500">{p.amount === null ? "Custom" : `$${p.amount}/mo`}</div></th>)}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 text-slate-600">{row.label}</td>
                  {PUBLIC_PLANS.map((p) => (
                    <td key={p.lookup_key} className="px-4 py-3 text-center">
                      {row.get ? <span className="text-slate-800 font-medium">{row.get(p)}</span>
                        : row.has(p) ? <Check className="w-4 h-4 text-emerald-500 mx-auto" />
                        : <Minus className="w-4 h-4 text-slate-300 mx-auto" />}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="px-4 py-3" />
                {PUBLIC_PLANS.map((p) => (
                  <td key={p.lookup_key} className="px-4 py-3 text-center">
                    <Button data-testid={`compare-get-started-${p.lookup_key}`} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => getStarted(p)}>{p.amount === null ? "Contact Sales" : "Get Started"}</Button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Best for */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-head text-2xl md:text-3xl font-medium text-slate-900">Which plan is right for you?</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PUBLIC_PLANS.map((p) => (
              <div key={p.lookup_key} className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-head font-semibold text-slate-900">{p.name}</h3>
                <p className="mt-2 text-sm text-slate-600">{p.best_for}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="font-head text-2xl md:text-3xl font-medium text-slate-900">Frequently asked questions</h2>
        <div className="mt-6 space-y-4" data-testid="plans-faq">
          {FAQ.map((f, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-semibold text-slate-900">{f.q}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-head text-3xl md:text-4xl font-semibold text-white">Need work done right now?</h2>
          <p className="mt-3 text-blue-100">Submit a maintenance request — no account required.</p>
          <Button data-testid="plans-cta-request-service" size="lg" className="mt-6 bg-white text-blue-700 hover:bg-slate-100 rounded-lg h-12 px-8" onClick={() => navigate("/request-service")}>
            Request Service <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-white">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center"><Building2 className="w-4 h-4" /></div>
            <span className="font-head font-bold">SJ Property Care</span>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} SJ Contract Solutions LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
